import React, { FunctionComponent } from 'react';
import { map } from 'ramda';
import withStyles from 'react-jss';
import { Grid } from 'semantic-ui-react';
import {
  FlexibleWidthXYPlot,
  VerticalGridLines,
  HorizontalGridLines,
  XAxis,
  YAxis,
  VerticalBarSeries,
  DiscreteColorLegend,
  Crosshair,
} from 'react-vis';

import { theme } from 'config';
import { ClassProp } from 'helpers/types';
import WithHint, { HintArgs } from 'scenes/Report/components/WithHint';
import { Scale } from 'scenes/Report/Report.helper';
import {
  tickFormatXAxis,
  MAX_TICK_TOTAL,
} from 'scenes/Report/scenes/ReportDetail/scenes/ThirdPartyReports/ThirdPartyReports.helper';

import styles from './BarChart.styles';

type Styles = ClassProp<ReturnType<typeof styles>>;
type Axis = { x: string; y: string };

type Legend = {
  title: string;
  color: string;
};

type Props = {
  classes: Styles;
  height: number;
  tickFormatYAxis: (value: number) => string;
  title: string;
  legendItems: Legend[];
  data: any;

  getAxis?: Axis;
  isDate?: boolean;
  hintFormat?: string;
  crosshair?: boolean;
};

const BAR_WIDTH = 0.65;

const XAxisDate = (data: any) => (
  <XAxis
    tickFormat={tickFormatXAxis}
    tickTotal={Math.min((data || []).length, MAX_TICK_TOTAL)}
  />
);

const hintFormatter = ({ x, y }: Axis) =>
  map(({ color, ...rest }: any) => ({
    color,
    x: rest[x],
    y: rest[y],
  }));

const CROSSHAIR_HEIGHT_GAP = 50;

const getCrosshairHeight = (
  [control, exposed]: Scale[],
  chartHeight: number,
  divider: number,
) =>
  ((chartHeight - CROSSHAIR_HEIGHT_GAP) / divider) *
    Math.abs(exposed.y - control.y) -
  5;

const renderCrosshair = (
  classes: Styles,
  chartHeight: number,
  [control, exposed]: Scale[],
) => {
  const isControlGreater = control.y > exposed.y;
  const divider = isControlGreater ? control.y : exposed.y;
  const height = getCrosshairHeight([control, exposed], chartHeight, divider);

  return (
    <Crosshair
      style={{
        line: {
          height,
          background: theme.darkGray,
        },
        box: { display: 'none' },
      }}
      values={[
        {
          x: isControlGreater ? exposed.x : control.x,
        },
      ]}
    >
      <div className={classes.crosshairTop} />

      <div
        className={classes.crosshairBottom}
        style={{
          top: height,
        }}
      />
    </Crosshair>
  );
};

const BarChart: FunctionComponent<Props> = ({
  classes,
  height,
  data: dataProp,
  legendItems,
  tickFormatYAxis,
  title,
  isDate,
  getAxis = { x: 'x', y: 'y' },
  hintFormat = '.2f',
  children,
  crosshair,
}) => {
  const data = hintFormatter(getAxis)(dataProp);

  return (
    <WithHint data={{ data }}>
      {({ onValueMouseOver, onValueMouseOut, renderHint }: HintArgs) => (
        <Grid container>
          <Grid.Row centered>
            <Grid.Column textAlign="center" width={14}>
              <FlexibleWidthXYPlot height={height} xType="ordinal">
                <VerticalGridLines />
                <HorizontalGridLines />
                <YAxis title={title} tickFormat={tickFormatYAxis} />
                {isDate ? XAxisDate(data) : <XAxis />}

                <VerticalBarSeries
                  color={theme.violet}
                  data={data}
                  barWidth={BAR_WIDTH}
                  onValueMouseOver={onValueMouseOver('data', hintFormat)}
                  onValueMouseOut={onValueMouseOut}
                  colorType="literal"
                />

                {crosshair && renderCrosshair(classes, height, data)}
                {renderHint()}
                {children}
              </FlexibleWidthXYPlot>
            </Grid.Column>
          </Grid.Row>

          <Grid.Row centered className={classes.legendRow}>
            <DiscreteColorLegend
              className={classes.legend}
              orientation="horizontal"
              items={legendItems}
            />
          </Grid.Row>
        </Grid>
      )}
    </WithHint>
  );
};

export default withStyles(styles)(BarChart);
